import { z } from 'zod';

// ------------------------------------------------------------
// Top Level Settings Groups
// ------------------------------------------------------------
const SETTINGS_GROUPS = {
    PROGRAM: 'PROGRAM',
    DOMAINS: 'DOMAINS',
    INVENTORY: 'INVENTORY',
    OPERATIONS: 'OPERATIONS',
    INTERNAL: 'INTERNAL',
};
const settingsGroupsValues = [
    SETTINGS_GROUPS.PROGRAM,
    SETTINGS_GROUPS.DOMAINS,
    SETTINGS_GROUPS.INVENTORY,
    SETTINGS_GROUPS.OPERATIONS,
    SETTINGS_GROUPS.INTERNAL,
];
const SettingsGroupsSchema = z.enum(settingsGroupsValues);
// ------------------------------------------------------------
// Sections within each Settings Group
// ------------------------------------------------------------
const PROGRAM_SECTIONS = {
    WEBSITE: 'PROGRAM_WEBSITE',
    PREORDER: 'PROGRAM_PREORDER',
    CHECKOUT: 'PROGRAM_CHECKOUT',
    PROCESSING: 'PROGRAM_PROCESSING',
    CONFIGURATOR: 'PROGRAM_CONFIGURATOR',
    COMMUNICATIONS: 'PROGRAM_COMMUNICATIONS',
    //MODAL: "PROGRAM_MODAL",
    //GENERAL: "PROGRAM_GENERAL", // for orphan top-level fields
};
const programSectionsValues = [
    PROGRAM_SECTIONS.WEBSITE,
    PROGRAM_SECTIONS.PREORDER,
    PROGRAM_SECTIONS.CHECKOUT,
    PROGRAM_SECTIONS.PROCESSING,
    PROGRAM_SECTIONS.CONFIGURATOR,
    PROGRAM_SECTIONS.COMMUNICATIONS,
];
const ProgramSectionsSchema = z.enum(programSectionsValues);
// DOMAINS sections based on DomainsServiceConfig nested sections
const DOMAINS_SECTIONS = {
    SHOP: 'DOMAINS_SHOP',
    SUBDOMAINS: 'DOMAINS_SUBDOMAINS',
    CRM: 'DOMAINS_CRM',
    BRANDING: 'DOMAINS_BRANDING',
    //GENERAL: "DOMAINS_GENERAL", // for orphan top-level fields
};
const domainsSectionsValues = [
    DOMAINS_SECTIONS.SHOP,
    DOMAINS_SECTIONS.SUBDOMAINS,
    DOMAINS_SECTIONS.CRM,
    DOMAINS_SECTIONS.BRANDING,
];
const DomainsSectionsSchema = z.enum(domainsSectionsValues);
// INVENTORY sections based on InventoryServiceConfig nested sections
const INVENTORY_SECTIONS = {
    DMS: 'INVENTORY_DMS',
    SCRAPING: 'INVENTORY_SCRAPING',
    FIELD_PRECEDENCE: 'INVENTORY_FIELD_PRECEDENCE',
    ADMIN_PORTAL: 'INVENTORY_ADMIN_PORTAL',
    GENERAL: 'INVENTORY_GENERAL',
};
const inventorySectionsValues = [
    INVENTORY_SECTIONS.DMS,
    INVENTORY_SECTIONS.SCRAPING,
    INVENTORY_SECTIONS.FIELD_PRECEDENCE,
    INVENTORY_SECTIONS.ADMIN_PORTAL,
    INVENTORY_SECTIONS.GENERAL,
];
const InventorySectionsSchema = z.enum(inventorySectionsValues);
// OPERATIONS sections - one per external service (all other services)
const OPERATIONS_SECTIONS = {
    SALE: 'OPERATIONS_SALE',
    DELIVERY: 'OPERATIONS_DELIVERY',
    PICKUP: 'OPERATIONS_PICKUP',
    ASSEMBLY: 'OPERATIONS_ASSEMBLY',
    INSPECTION: 'OPERATIONS_INSPECTION',
    FREIGHT: 'OPERATIONS_FREIGHT',
    TITLING: 'OPERATIONS_TITLING',
    REGISTRATION: 'OPERATIONS_REGISTRATION',
    COVERAGE_FULFILLMENT: 'OPERATIONS_COVERAGE_FULFILLMENT',
    FINANCING_BROKERAGE: 'OPERATIONS_FINANCING_BROKERAGE',
    TARIFF: 'OPERATIONS_TARIFF',
    PROCESSING_FEES: 'OPERATIONS_PROCESSING_FEES',
    TEMP_TAG: 'OPERATIONS_TEMP_TAG',
    // COVERAGE_TAX: "OPERATIONS_COVERAGE_TAX",
    // EXTERNAL_TAX: "OPERATIONS_EXTERNAL_TAX",
    // EXTERNAL_FEES: "OPERATIONS_EXTERNAL_FEES",
    // ORIGINATOR: "OPERATIONS_ORIGINATOR",
};
const operationsSectionsValues = [
    OPERATIONS_SECTIONS.SALE,
    OPERATIONS_SECTIONS.DELIVERY,
    OPERATIONS_SECTIONS.PICKUP,
    OPERATIONS_SECTIONS.ASSEMBLY,
    OPERATIONS_SECTIONS.INSPECTION,
    OPERATIONS_SECTIONS.FREIGHT,
    OPERATIONS_SECTIONS.TITLING,
    OPERATIONS_SECTIONS.REGISTRATION,
    OPERATIONS_SECTIONS.COVERAGE_FULFILLMENT,
    OPERATIONS_SECTIONS.FINANCING_BROKERAGE,
    OPERATIONS_SECTIONS.TARIFF,
    OPERATIONS_SECTIONS.PROCESSING_FEES,
    OPERATIONS_SECTIONS.TEMP_TAG,
];
const OperationsSectionsSchema = z.enum(operationsSectionsValues);
// INTERNAL sections - one per internal service
const INTERNAL_SECTIONS = {
    INTERNAL_FEES: 'INTERNAL_INTERNAL_FEES',
    PLATFORM: 'INTERNAL_PLATFORM',
    FINANCING_BROKERAGE_SUBVENTION: 'INTERNAL_FINANCING_BROKERAGE_SUBVENTION',
    COVERAGE_VENDOR: 'INTERNAL_COVERAGE_VENDOR',
    COVERAGE_PLATFORM: 'INTERNAL_COVERAGE_PLATFORM',
    LICENSURE: 'INTERNAL_LICENSURE',
    FINANCING: 'INTERNAL_FINANCING',
    FINANCING_BROKERAGE_PLATFORM: 'INTERNAL_FINANCING_BROKERAGE_PLATFORM',
};
const internalSectionsValues = [
    INTERNAL_SECTIONS.INTERNAL_FEES,
    INTERNAL_SECTIONS.PLATFORM,
    INTERNAL_SECTIONS.FINANCING_BROKERAGE_SUBVENTION,
    INTERNAL_SECTIONS.COVERAGE_VENDOR,
    INTERNAL_SECTIONS.COVERAGE_PLATFORM,
    INTERNAL_SECTIONS.LICENSURE,
    INTERNAL_SECTIONS.FINANCING,
    INTERNAL_SECTIONS.FINANCING_BROKERAGE_PLATFORM,
];
const InternalSectionsSchema = z.enum(internalSectionsValues);
const SECTIONS = {
    ...PROGRAM_SECTIONS,
    ...DOMAINS_SECTIONS,
    ...INVENTORY_SECTIONS,
    ...INTERNAL_SECTIONS,
    ...OPERATIONS_SECTIONS,
};
const sectionsValues = [
    ...programSectionsValues,
    ...domainsSectionsValues,
    ...inventorySectionsValues,
    ...internalSectionsValues,
    ...operationsSectionsValues,
];
const SectionsSchema = z.enum(sectionsValues);
// ------------------------------------------------------------
// Subsections within each Section
// ------------------------------------------------------------
const SUBSECTIONS = {
    // Sales
    SALES_PRIMARY_CONTRACT_SIGNER: 'SALES_PRIMARY_CONTRACT_SIGNER',
    // Program Owner - Checkout
    CHECKOUT_ASSIGNMENT: 'CHECKOUT_ASSIGNMENT',
    CHECKOUT_FULFILLMENT: 'CHECKOUT_FULFILLMENT',
    CHECKOUT_PAYMENT: 'CHECKOUT_PAYMENT',
    CHECKOUT_STATES_SUPPORTED: 'CHECKOUT_STATES_SUPPORTED',
    CHECKOUT_DISCLOSURES: 'CHECKOUT_DISCLOSURES',
};
const subsectionsValues = [
    SUBSECTIONS.SALES_PRIMARY_CONTRACT_SIGNER,
    SUBSECTIONS.CHECKOUT_ASSIGNMENT,
    SUBSECTIONS.CHECKOUT_FULFILLMENT,
    SUBSECTIONS.CHECKOUT_PAYMENT,
    SUBSECTIONS.CHECKOUT_STATES_SUPPORTED,
    SUBSECTIONS.CHECKOUT_DISCLOSURES,
];
const SubsectionsSchema = z.enum(subsectionsValues);
// Temp mechanism for filtering out Ekho-only sections from config mgmt hub (future: AuthZ will control visibility)
const EKHO_ONLY_SECTIONS = [
    // Program sections
    PROGRAM_SECTIONS.WEBSITE,
    PROGRAM_SECTIONS.PROCESSING,
    PROGRAM_SECTIONS.CONFIGURATOR,
    PROGRAM_SECTIONS.COMMUNICATIONS,
    // Domains sections
    ...Object.values(DOMAINS_SECTIONS),
    // Operations sections
    OPERATIONS_SECTIONS.TITLING,
    OPERATIONS_SECTIONS.REGISTRATION,
    OPERATIONS_SECTIONS.COVERAGE_FULFILLMENT,
    OPERATIONS_SECTIONS.FINANCING_BROKERAGE,
    OPERATIONS_SECTIONS.TARIFF,
    OPERATIONS_SECTIONS.PROCESSING_FEES,
    OPERATIONS_SECTIONS.TEMP_TAG,
    // Internal sections
    ...Object.values(INTERNAL_SECTIONS),
];

export { DOMAINS_SECTIONS, DomainsSectionsSchema, EKHO_ONLY_SECTIONS, INTERNAL_SECTIONS, INVENTORY_SECTIONS, InternalSectionsSchema, InventorySectionsSchema, OPERATIONS_SECTIONS, OperationsSectionsSchema, PROGRAM_SECTIONS, ProgramSectionsSchema, SECTIONS, SETTINGS_GROUPS, SUBSECTIONS, SectionsSchema, SettingsGroupsSchema, SubsectionsSchema };
//# sourceMappingURL=ServiceSections.js.map
